import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { useAuth } from '../../contexts/AuthContext';
import ConstructionContextFilter from '../../components/ConstructionContextFilter';
import ConstructionBreadcrumb from '../../components/ConstructionBreadcrumb';
import BOQItemDrawer from '../../components/BOQItemDrawer';
import { ClipboardList, Loader2, Upload, ChevronLeft, ChevronRight, Boxes, Plus } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const fmt = (v) => (v === null || v === undefined ? '—' : v);
const TABS = [{ k: 'dashboard', l: 'Dashboard' }, { k: 'explorer', l: 'BOQ Explorer' }, { k: 'materials', l: 'Materials' }, { k: 'revisions', l: 'Revisions' }, { k: 'reports', l: 'Reports' }];
const REPORT_TYPES = [
  { k: 'reconciliation', l: 'Reconciliation' }, { k: 'material-variance', l: 'Material Variance' },
  { k: 'evidence', l: 'Quantity Evidence' }, { k: 'mapping-status', l: 'Mapping Status' },
];

const BOQIntelligence = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'CompanyAdmin' || user?.role === 'SuperAdmin';
  const canReview = isAdmin || user?.role === 'User'; // User has quantity.observe; review gated server-side
  const [tab, setTab] = useState('dashboard');

  const [dashboard, setDashboard] = useState(null);
  const [boqs, setBoqs] = useState([]);
  const [activeBoqId, setActiveBoqId] = useState('');
  const [items, setItems] = useState({ items: [], total: 0, page: 1, pageSize: 25 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [ctxFilter, setCtxFilter] = useState({});
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openItem, setOpenItem] = useState(null);
  const [newMat, setNewMat] = useState({ name: '', materialCode: '', baseUom: '', aiRecognitionLabels: '' });
  const [revA, setRevA] = useState('');
  const [revB, setRevB] = useState('');
  const [compare, setCompare] = useState(null);
  const [reportType, setReportType] = useState('reconciliation');
  const [reportData, setReportData] = useState(null);

  const loadDashboard = useCallback(async () => {
    const d = await axios.get(`${API}/projects/${projectId}/boq-dashboard`).then((r) => r.data);
    setDashboard(d);
    const list = await axios.get(`${API}/projects/${projectId}/boqs`).then((r) => r.data);
    setBoqs(list);
    const active = list.find((b) => b.status === 'ACTIVE') || list[0];
    if (active && !activeBoqId) setActiveBoqId(active.id);
  }, [projectId, activeBoqId]);

  const loadItems = useCallback(async () => {
    if (!activeBoqId) { setItems({ items: [], total: 0, page: 1, pageSize: 25 }); return; }
    const params = { page, pageSize: 25, search: search || undefined, ...ctxFilter };
    const d = await axios.get(`${API}/projects/${projectId}/boqs/${activeBoqId}/items`, { params }).then((r) => r.data);
    setItems(d);
  }, [projectId, activeBoqId, page, search, ctxFilter]);

  const loadMaterials = useCallback(async () => {
    setMaterials(await axios.get(`${API}/projects/${projectId}/materials-master`).then((r) => r.data));
  }, [projectId]);

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        if (tab === 'dashboard') await loadDashboard();
        else if (tab === 'explorer') { await loadDashboard(); await loadItems(); }
        else if (tab === 'materials') await loadMaterials();
        else if (tab === 'revisions') { await loadDashboard(); }
        else if (tab === 'reports') { await loadDashboard(); await runReport('reconciliation'); }
      } catch (e) { toast.error('Failed to load BOQ data'); } finally { setLoading(false); }
    })();
  }, [tab, loadDashboard, loadItems, loadMaterials]); // eslint-disable-line

  const runCompare = async () => {
    if (!revA || !revB) { toast.error('Pick two revisions'); return; }
    try { setCompare((await axios.get(`${API}/projects/${projectId}/boqs/compare`, { params: { base: revA, target: revB } })).data); }
    catch (e) { toast.error('Compare failed'); }
  };

  const runReport = async (type) => {
    setReportType(type);
    try { setReportData((await axios.get(`${API}/projects/${projectId}/boq-reports/${type}`, { params: { boqId: activeBoqId || undefined } })).data); }
    catch (e) { toast.error('Report failed'); }
  };

  const downloadReport = (fmt) => {
    const token = localStorage.getItem('accessToken');
    const url = `${API}/projects/${projectId}/boq-reports/${reportType}?format=${fmt}${activeBoqId ? `&boqId=${activeBoqId}` : ''}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => { const a = document.createElement('a'); a.href = URL.createObjectURL(b); a.download = `boq-${reportType}.${fmt}`; a.click(); })
      .catch(() => toast.error('Download failed'));
  };

  useEffect(() => { if (tab === 'explorer') loadItems(); }, [activeBoqId, page, search, ctxFilter]); // eslint-disable-line

  const addMaterial = async () => {
    if (!newMat.name.trim()) { toast.error('Name required'); return; }
    try {
      await axios.post(`${API}/projects/${projectId}/materials-master`, {
        ...newMat, aiRecognitionLabels: newMat.aiRecognitionLabels.split(',').map((s) => s.trim()).filter(Boolean),
      });
      toast.success('Material added'); setNewMat({ name: '', materialCode: '', baseUom: '', aiRecognitionLabels: '' }); await loadMaterials();
    } catch (e) { toast.error('Failed to add material'); }
  };

  const kpiCards = dashboard ? [
    ['Active BOQ Items', dashboard.activeItems], ['Mapped Items', dashboard.mappedItems],
    ['Unmapped Items', dashboard.unmappedItems], ['Items w/ Evidence', dashboard.itemsWithEvidence],
    ['Pending Review', dashboard.pendingReview], ['Damaged/Rejected', dashboard.damagedRejectedObservations],
  ] : [];

  const totalPages = Math.max(1, Math.ceil((items.total || 0) / 25));

  return (
    <Layout>
      <div className="space-y-6">
        <button onClick={() => navigate('/app/projects')} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700" data-testid="boq-back"><ChevronLeft className="w-4 h-4" /> Projects</button>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2"><ClipboardList className="w-7 h-7 text-blue-600" /> BOQ Intelligence</h1>
            <p className="text-slate-600 mt-1">Plan vs observed quantities, reconciled with full traceability.</p>
          </div>
          {isAdmin && (
            <button onClick={() => navigate(`/app/projects/${projectId}/boq-import`)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2" data-testid="boq-import-btn"><Upload className="w-4 h-4" /> Import BOQ</button>
          )}
        </div>

        <div className="flex gap-2 border-b border-slate-200" data-testid="boq-tabs">
          {TABS.map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === t.k ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-testid={`boq-tab-${t.k}`}>{t.l}</button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…</div>
        ) : tab === 'dashboard' ? (
          <>
            {!dashboard?.activeBoq && <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 text-sm text-slate-600" data-testid="boq-no-active">No ACTIVE BOQ yet. {isAdmin ? 'Import and activate a BOQ to begin.' : 'Ask an admin to import a BOQ.'}</div>}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="boq-kpis">
              {kpiCards.map(([label, val]) => (
                <div key={label} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
                  <p className="text-2xl font-bold text-slate-900">{fmt(val)}</p>
                  <p className="text-xs text-slate-500 mt-1">{label}</p>
                </div>
              ))}
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="boq-recent-reviews">
              <h3 className="font-semibold text-slate-800 mb-3">Recent Quantity Reviews</h3>
              {(dashboard?.recentReviews || []).length === 0 ? <p className="text-sm text-slate-400">No reviews yet.</p> : (
                <div className="space-y-2">
                  {dashboard.recentReviews.map((r) => (
                    <div key={r.id} className="flex items-center justify-between text-sm border-b border-slate-50 pb-2">
                      <span>{r.observationType} · {r.sourceType}</span>
                      <span className="text-slate-500">AI {fmt(r.aiOriginalQuantity)} → reviewed {fmt(r.reviewedQuantity)} <span className="text-xs">({r.reviewStatus})</span></span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : tab === 'explorer' ? (
          <>
            <div className="flex items-center gap-3 flex-wrap">
              <select value={activeBoqId} onChange={(e) => { setActiveBoqId(e.target.value); setPage(1); }} className="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white" data-testid="boq-select">
                {boqs.length === 0 && <option value="">No BOQs</option>}
                {boqs.map((b) => <option key={b.id} value={b.id}>{b.name} — {b.revision} ({b.status})</option>)}
              </select>
              <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Search item code / description" className="px-3 py-2 border border-slate-300 rounded-lg text-sm flex-1 min-w-[200px]" data-testid="boq-search" />
            </div>
            <ConstructionContextFilter projectId={projectId} value={ctxFilter} onChange={(v) => { setCtxFilter(v); setPage(1); }} testId="boq-context-filter" />

            <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="boq-items-table">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-slate-500 border-b border-slate-100"><th className="px-4 py-2">Code</th><th className="px-4 py-2">Description</th><th className="px-3 py-2">Unit</th><th className="px-3 py-2 text-right">Planned</th><th className="px-4 py-2">Context</th></tr></thead>
                <tbody>
                  {items.items.length === 0 ? (
                    <tr><td colSpan="5" className="px-4 py-8 text-center text-slate-400" data-testid="boq-items-empty">No items.</td></tr>
                  ) : items.items.map((it) => (
                    <tr key={it.id} onClick={() => setOpenItem(it.id)} className="border-b border-slate-50 hover:bg-blue-50 cursor-pointer" data-testid="boq-item-row">
                      <td className="px-4 py-3 font-medium text-slate-800">{it.itemCode || '—'}</td>
                      <td className="px-4 py-3 text-slate-700 max-w-xs truncate">{it.description}</td>
                      <td className="px-3 py-3">{it.unit || it.rawUnit || '—'}</td>
                      <td className="px-3 py-3 text-right">{fmt(it.plannedQuantity)}</td>
                      <td className="px-4 py-3"><ConstructionBreadcrumb projectId={projectId} context={it.constructionContext} fallback="Unmapped" testId={`boq-row-ctx-${it.id}`} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between text-sm text-slate-500">
              <span>{items.total} items</span>
              <div className="flex items-center gap-2">
                <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="p-1 disabled:opacity-30" data-testid="boq-prev-page"><ChevronLeft className="w-4 h-4" /></button>
                <span>Page {page} / {totalPages}</span>
                <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="p-1 disabled:opacity-30" data-testid="boq-next-page"><ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>
          </>
        ) : tab === 'revisions' ? (
          <div className="space-y-4" data-testid="boq-revisions">
            <div className="flex items-center gap-3 flex-wrap bg-white border border-slate-200 rounded-xl p-4">
              <select value={revA} onChange={(e) => setRevA(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white" data-testid="rev-a-select">
                <option value="">Revision A</option>
                {boqs.map((b) => <option key={b.id} value={b.id} disabled={b.id === revB}>{b.name} — {b.revision} ({b.status})</option>)}
              </select>
              <span className="text-slate-400">vs</span>
              <select value={revB} onChange={(e) => setRevB(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white" data-testid="rev-b-select">
                <option value="">Revision B</option>
                {boqs.map((b) => <option key={b.id} value={b.id} disabled={b.id === revA}>{b.name} — {b.revision} ({b.status})</option>)}
              </select>
              <button onClick={runCompare} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm" data-testid="rev-compare-btn">Compare</button>
            </div>
            {compare && (
              <div className="space-y-3" data-testid="rev-compare-result">
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-white rounded-xl border border-slate-200 p-4"><p className="text-2xl font-bold text-emerald-600">{compare.summary.added}</p><p className="text-xs text-slate-500">Added</p></div>
                  <div className="bg-white rounded-xl border border-slate-200 p-4"><p className="text-2xl font-bold text-red-500">{compare.summary.removed}</p><p className="text-xs text-slate-500">Removed</p></div>
                  <div className="bg-white rounded-xl border border-slate-200 p-4"><p className="text-2xl font-bold text-blue-600">{compare.summary.changed}</p><p className="text-xs text-slate-500">Changed</p></div>
                </div>
                {compare.changed.length > 0 && (
                  <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <h4 className="font-semibold text-slate-800 mb-2">Changed Items</h4>
                    {compare.changed.map((c) => (
                      <div key={c.itemCode} className="border-b border-slate-50 py-2 text-sm" data-testid="rev-changed-row">
                        <span className="font-medium">{c.itemCode}</span>
                        {Object.entries(c.diffs).map(([field, vals]) => (
                          <div key={field} className="text-xs text-slate-500 ml-2">{field}: <span className="text-red-500">{JSON.stringify(vals[0])}</span> → <span className="text-emerald-600">{JSON.stringify(vals[1])}</span></div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
                {compare.added.length > 0 && <div className="bg-white rounded-xl border border-slate-200 p-4 text-sm" data-testid="rev-added"><h4 className="font-semibold text-emerald-700 mb-1">Added</h4>{compare.added.map((i) => <div key={i.id}>{i.itemCode} · {i.description}</div>)}</div>}
                {compare.removed.length > 0 && <div className="bg-white rounded-xl border border-slate-200 p-4 text-sm" data-testid="rev-removed"><h4 className="font-semibold text-red-600 mb-1">Removed</h4>{compare.removed.map((i) => <div key={i.id}>{i.itemCode} · {i.description}</div>)}</div>}
              </div>
            )}
          </div>
        ) : tab === 'reports' ? (
          <div className="space-y-4" data-testid="boq-reports">
            <div className="flex items-center gap-2 flex-wrap">
              {REPORT_TYPES.map((r) => (
                <button key={r.k} onClick={() => runReport(r.k)} className={`px-3 py-2 rounded-lg text-sm ${reportType === r.k ? 'bg-blue-600 text-white' : 'bg-white border border-slate-300 text-slate-600'}`} data-testid={`report-type-${r.k}`}>{r.l}</button>
              ))}
              <div className="ml-auto flex gap-2">
                <button onClick={() => downloadReport('csv')} className="px-3 py-2 bg-slate-100 rounded-lg text-sm" data-testid="report-download-csv">CSV</button>
                <button onClick={() => downloadReport('xlsx')} className="px-3 py-2 bg-slate-100 rounded-lg text-sm" data-testid="report-download-xlsx">XLSX</button>
              </div>
            </div>
            {reportData && (
              <>
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 text-xs text-slate-600" data-testid="report-meta">
                  <b>{reportData.meta.project}</b> · BOQ: {reportData.meta.boqName || '—'} {reportData.meta.boqRevision || ''} · Generated {(reportData.meta.generatedAt || '').slice(0, 16)} by {reportData.meta.generatedBy}. {reportData.meta.quantityLegend}
                </div>
                <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="report-table">
                  <table className="w-full text-xs">
                    <thead><tr className="text-left text-slate-500 border-b border-slate-100">{reportData.columns.map((c) => <th key={c} className="px-3 py-2 whitespace-nowrap">{c}</th>)}</tr></thead>
                    <tbody>
                      {reportData.rows.length === 0 ? <tr><td colSpan={reportData.columns.length} className="px-3 py-6 text-center text-slate-400" data-testid="report-empty">No data</td></tr> :
                        reportData.rows.map((row, i) => (
                          <tr key={i} className="border-b border-slate-50" data-testid="report-row">
                            {reportData.columns.map((c) => <td key={c} className="px-3 py-2 whitespace-nowrap">{row[c] === null || row[c] === undefined ? '—' : String(row[c])}</td>)}
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>

        ) : (
          <>
            {isAdmin && (
              <div className="bg-white rounded-xl border border-slate-200 p-4 grid grid-cols-1 sm:grid-cols-5 gap-3 items-end" data-testid="material-form">
                <div><label className="text-xs text-slate-500">Name</label><input value={newMat.name} onChange={(e) => setNewMat({ ...newMat, name: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="material-name-input" /></div>
                <div><label className="text-xs text-slate-500">Code</label><input value={newMat.materialCode} onChange={(e) => setNewMat({ ...newMat, materialCode: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" /></div>
                <div><label className="text-xs text-slate-500">Base UOM</label><input value={newMat.baseUom} onChange={(e) => setNewMat({ ...newMat, baseUom: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" placeholder="NOS, M2…" /></div>
                <div><label className="text-xs text-slate-500">AI labels (comma)</label><input value={newMat.aiRecognitionLabels} onChange={(e) => setNewMat({ ...newMat, aiRecognitionLabels: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" /></div>
                <button onClick={addMaterial} className="px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center justify-center gap-1" data-testid="material-add-btn"><Plus className="w-4 h-4" /> Add</button>
              </div>
            )}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="materials-list">
              {materials.length === 0 ? <div className="p-8 text-center text-slate-400" data-testid="materials-empty">No materials yet.</div> : (
                <table className="w-full text-sm"><thead><tr className="text-left text-slate-500 border-b border-slate-100"><th className="px-4 py-2">Material</th><th className="px-3 py-2">Code</th><th className="px-3 py-2">Base UOM</th><th className="px-3 py-2">AI Labels</th></tr></thead>
                  <tbody>{materials.map((m) => (
                    <tr key={m.id} className="border-b border-slate-50" data-testid="material-row">
                      <td className="px-4 py-3 font-medium text-slate-800 flex items-center gap-2"><Boxes className="w-4 h-4 text-slate-400" />{m.name}</td>
                      <td className="px-3 py-3 text-slate-500">{m.code || '—'}</td>
                      <td className="px-3 py-3">{m.baseUom || '—'}</td>
                      <td className="px-3 py-3 text-xs text-slate-400">{(m.aiRecognitionLabels || []).join(', ') || '—'}</td>
                    </tr>
                  ))}</tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>

      {openItem && (
        <BOQItemDrawer projectId={projectId} itemId={openItem} canReview={canReview} canMap={isAdmin}
          onClose={() => setOpenItem(null)} onChanged={() => { loadItems(); loadDashboard(); }} />
      )}
    </Layout>
  );
};

export default BOQIntelligence;
